import * as React from 'react';

import ClearIcon from '@mui/icons-material/Clear';
import SearchIcon from '@mui/icons-material/Search';
import TuneOutlinedIcon from '@mui/icons-material/TuneOutlined';
import {
  Box,
  ButtonGroup,
  IconButton,
  InputAdornment,
  OutlinedInput,
  Stack,
  Typography,
  debounce,
} from '@mui/material';

import styled from '@emotion/styled';
import { ShowFnOutput, useModal } from 'mui-modal-provider';

import Button from '../../components/Button/Button';
import SavedButton from '../../components/Button/SavedButton';
import { InviteDialog } from '../../components/Dialogs';
import CollabsFiltersDialog from '../../components/Dialogs/CollabsFilters/CollabsFiltersDialog';
import CollabsSentDialog from '../../components/Dialogs/CollabsSent/CollabsSentDialog';
import { InviteDialogProps } from '../../components/Dialogs/CreateInvite/InviteDialog';
import { ProductInviteDialog } from '../../components/Dialogs/CreateProductInvite/ProductInviteDialog';
import HowCollabsWorksDialog from '../../components/Dialogs/HowCollabsWorks/HowCollabsWorksDialog';
import TitleWithIcon from '../../components/Dialogs/TitleWithIcon';
import PageTitle from '../../components/PageTitle/PageTitle';
import { useNotificationsContext } from '../../contexts/NotificationsContext';
import { VenueListUsersFilter } from '../../graphql/API';
import { useLocalStorageState } from '../../hooks/useLocalStorageState';
import { markHighlight } from '../../redux/features/invite/inviteSlice';
import { useAppDispatch, useAppSelector } from '../../redux/hooks';
import Icons from '../../themes/icons';
import { formatNumberWithCommas } from '../../utils/formatter';
import { VENUE_MANUAL_TAGS } from '../../utils/venue-tags';
import { extractSubscriptionPlan } from '../SubscriptionPlans/common/helper';
import { PageView } from './Collabs';
import InviteSentButton from './components/InviteSentButton';
import { CollabReducer } from './reducer';

type HeaderProps = {
  reducer: CollabReducer;
  inScroll: boolean;
  handleInvite?: () => void;
  isLoading?: boolean;
  isCollabFlowActive?: boolean;
  pageView?: PageView;
  setPageView: React.Dispatch<React.SetStateAction<PageView>>;
};

type CountProps = {
  count: number;
};
const Count: React.FC<CountProps> = ({ count }) => (
  <Box component='span' sx={{ color: count > 0 ? '#BA8AFF' : undefined }}>
    {count}
  </Box>
);

const Header: React.FC<HeaderProps> = ({
  reducer,
  inScroll,
  handleInvite,
  isLoading = false,
  isCollabFlowActive = false,
  pageView = 'grid',
  setPageView,
}) => {
  const [collabState, dispatch] = reducer;
  const {
    filters,
    selected: { users: selectedUsers, values: selectedValues },
  } = collabState;

  const venueState = useAppSelector((state) => state.venue.value);
  const { subscription } = useAppSelector((state) => state.subscription.value);

  const isOnlineBrand = venueState?.tags?.includes(VENUE_MANUAL_TAGS.Online);

  const inviteModalRef = React.useRef<ShowFnOutput<InviteDialogProps>>();
  const [productInviteDialog, setProductInviteDialog] = React.useState(false);
  const [collabInviteDialog, setCollabInviteDialog] = React.useState(false);

  const { showModal } = useModal();
  const { lastCollabUpdatedAtTime, invite, handleUpdateInvite } = useNotificationsContext() || {};

  const highlight = useAppSelector((state) => state.invite.highlight);
  const appDispatch = useAppDispatch();
  const [searchText, setSearchText] = useLocalStorageState('collab-search-text', '');

  const searchDebounce = React.useMemo(
    () =>
      debounce(
        (searchText: string) => dispatch({ type: 'SET_FILTERS', payload: { searchText } }),
        500,
      ),
    [dispatch],
  );

  React.useEffect(() => {
    searchDebounce(searchText);
  }, [searchText, searchDebounce]);

  const selected = React.useMemo(
    () => ({
      count: selectedUsers.length,
    }),
    [selectedUsers],
  );

  const handleCreateInviteDialog = React.useCallback(() => {
    if (venueState?.tags?.includes(VENUE_MANUAL_TAGS.Online)) {
      setProductInviteDialog((prev) => !prev);
    } else {
      setCollabInviteDialog((prev) => !prev);
    }
  }, [venueState?.tags]);

  const handleFilterDialog = React.useCallback(
    () =>
      showModal(CollabsFiltersDialog, {
        titleComponent: <TitleWithIcon title='Filters' icon={<TuneOutlinedIcon />} />,
        onFilterChange: (f) => dispatch({ type: 'SET_FILTERS', payload: f }),
        filters,
      }),
    [filters, dispatch, showModal],
  );

  const handleHowCollabsWorksDialog = () => {
    showModal(HowCollabsWorksDialog, {});
  };

  const handleCollabSentInviteDialog = React.useCallback(() => {
    showModal(CollabsSentDialog, {
      handleUpdateLastInvite: handleUpdateInvite,
    });
  }, [handleUpdateInvite, showModal]);

  const handleTextOnChange = React.useCallback<React.ChangeEventHandler<HTMLTextAreaElement>>(
    (e) => setSearchText(e.target.value),
    [setSearchText],
  );

  const handleClick = React.useCallback(() => {
    dispatch({ type: 'SET_FILTERS', payload: { saved: filters?.saved ? undefined : true } });
  }, [filters, dispatch]);

  const isAnyFilterActive = React.useCallback(
    (filters: Partial<VenueListUsersFilter> | undefined) => {
      return (
        filters &&
        ((filters['minFollowers'] && filters['minFollowers'] > 0) ||
          ((filters['marketRegion'] && filters['marketRegion']?.length) || 0) > 0 ||
          filters['lifestyle'] !== undefined ||
          filters['modelType'] !== undefined ||
          filters['instagram'] !== undefined ||
          (filters['market'] === false && !isOnlineBrand))
      );
    },
    [isOnlineBrand],
  );

  const handleClearSearchText = React.useCallback(() => {
    setSearchText('');
  }, [setSearchText]);

  const userDeletionHandle = (userID: number) => {
    const newSelection = (selectedValues || []).filter((x) => Number(x) !== userID);
    const newUsers = (selectedUsers || []).filter((x) => Number(x.id) !== userID);

    dispatch({
      type: 'SET_SELECTED_USERS',
      payload: {
        values: newSelection,
        users: newUsers,
      },
    });
  };

  const handlePageViewChange = React.useCallback(
    (view: PageView) => {
      setPageView(view);
    },
    [setPageView],
  );

  const subscriptionPlan = React.useMemo(() => {
    const [plan] = extractSubscriptionPlan(subscription?.sku ?? '');

    return plan;
  }, [subscription]);

  return (
    <Box
      sx={{
        position: 'sticky',
        backgroundColor: 'white',
        px: 3,
        py: 2,
        display: isCollabFlowActive ? 'none' : 'block',
        ...(inScroll ? { boxShadow: '0px 1px 20px #00000026' } : undefined),
      }}
    >
      <PageTitle
        titleComponent={
          <Box display={'flex'} alignItems={'center'} gap={'10px'}>
            <Typography fontWeight='500' fontSize={'22px'}>
              Paid Collabs
            </Typography>
            <LearnMoreContainer onClick={handleHowCollabsWorksDialog}>
              <IconButton size='small' color='primary'>
                <Icons.InfoCircleFillIcon style={{ width: '17px', height: '17px', fill: '#111' }} />
              </IconButton>
              <LearnMoreButtonText>Learn more</LearnMoreButtonText>
            </LearnMoreContainer>
          </Box>
        }
        rightComponent={
          <Stack direction='row' spacing={2}>
            <ButtonGroup variant='outlined' size='small'>
              <Button
                sx={{
                  border: '1px solid #F6F6F6',
                  ...(pageView === 'grid' && { color: '#765BC6', backgroundColor: '#F1EFF9' }),
                }}
                onClick={() => handlePageViewChange('grid')}
              >
                <Icons.GridIconV2 />
              </Button>
              <Button
                sx={{
                  border: '1px solid #F6F6F6',
                  ...(pageView === 'list' && { color: '#765BC6', backgroundColor: '#F1EFF9' }),
                }}
                onClick={() => handlePageViewChange('list')}
              >
                <Icons.ListIconV2 />
              </Button>
            </ButtonGroup>
            <SavedButton
              onClick={handleClick}
              active={Boolean(filters?.saved)}
              disabled={
                venueState?.tags?.includes(VENUE_MANUAL_TAGS.BrandPlans) && !subscriptionPlan
              }
              sx={{ border: '1px solid #F6F6F6', px: 2, py: 0.2 }}
            >
              Saved
            </SavedButton>
            <InviteSentButton
              id='btn-list-invite'
              highlight={
                highlight ||
                (lastCollabUpdatedAtTime && invite?.updatedAt !== lastCollabUpdatedAtTime)
                  ? true
                  : false
              }
              onClick={handleCollabSentInviteDialog}
              sx={{ border: '1px solid #F6F6F6', px: 2, py: 0.2 }}
            />
            <Button
              id='btn-filter'
              startIcon={
                <TuneOutlinedIcon htmlColor={isAnyFilterActive(filters) ? '#765BC6' : ''} />
              }
              onClick={handleFilterDialog}
              sx={{ border: '1px solid #F6F6F6', px: 2, py: 0.2 }}
            >
              <span style={{ color: isAnyFilterActive(filters) ? '#765BC6' : '' }}>Filters</span>
            </Button>
            <Stack direction='row' spacing={1.5}>
              <OutlinedInput
                id='outlined-adornment-amount'
                placeholder='Search a member...'
                startAdornment={
                  <InputAdornment position='start'>
                    <SearchIcon />
                  </InputAdornment>
                }
                endAdornment={
                  searchText && (
                    <InputAdornment position='end'>
                      <ClearIcon cursor='pointer' onClick={handleClearSearchText} />
                    </InputAdornment>
                  )
                }
                defaultValue={filters?.searchText}
                value={searchText}
                onChange={handleTextOnChange}
                size='small'
                disabled={
                  venueState?.tags?.includes(VENUE_MANUAL_TAGS.BrandPlans) && !subscriptionPlan
                }
                fullWidth
                sx={{
                  minWidth: '290px',
                  border: '1px solid #F6F6F6',
                  boxShadow: 'none',
                  '& .MuiOutlinedInput-notchedOutline': { border: 'none' },
                }}
              />
              <Button
                id='btn-create-invite'
                variant='contained'
                disabled={
                  !selectedUsers.length ||
                  (venueState?.tags?.includes(VENUE_MANUAL_TAGS.BrandPlans) && !subscriptionPlan) ||
                  isLoading
                }
                sx={{ minWidth: '170px' }}
                onClick={handleInvite}
              >
                Invite (<Count count={selected.count} />)
              </Button>
            </Stack>
          </Stack>
        }
      />

      <Typography component={Stack} spacing={2} direction='row' mt={1} alignItems={'center'}>
        {selected.count > 0 && (
          <>
            <Box component='div' display={'flex'} alignItems={'center'} gap={'10px'}>
              <EstimatedCost>
                Post minimum cost: ${formatNumberWithCommas(selectedUsers.length * 10)}
              </EstimatedCost>
            </Box>
          </>
        )}
        {selected.count > 0 && (
          <Button
            sx={{
              p: 0,
              lineHeight: 'unset',
              minWidth: 'unset',
              textDecoration: 'underline',
              ':hover': { backgroundColor: 'unset', textDecoration: 'underline' },
            }}
            onClick={() => {
              dispatch({ type: 'RESET_SELECTED_USERS' });
              if (isOnlineBrand) {
                dispatch({ type: 'RESET_SELECTED_FILTERS_ONLINE' });
              } else {
                dispatch({ type: 'RESET_SELECTED_FILTERS' });
              }
            }}
            disabled={venueState?.tags?.includes(VENUE_MANUAL_TAGS.BrandPlans) && !subscriptionPlan}
          >
            Reset
          </Button>
        )}
      </Typography>

      <ProductInviteDialog
        open={productInviteDialog}
        onClose={() => setProductInviteDialog((prev) => !prev)}
        title='Invite'
        users={selectedUsers}
        modalRef={inviteModalRef}
        onSuccess={() => {
          appDispatch(markHighlight(true));
          dispatch({ type: 'RESET_SELECTED_USERS' });
        }}
        handleUserDeletion={(userID: number) => {
          userDeletionHandle(userID);
        }}
      />

      <InviteDialog
        open={collabInviteDialog}
        onClose={() => setCollabInviteDialog((prev) => !prev)}
        title='Invite'
        users={selectedUsers}
        modalRef={inviteModalRef}
        onSuccess={() => {
          appDispatch(markHighlight(true));
          dispatch({ type: 'RESET_SELECTED_USERS' });
        }}
        handleUserDeletion={(userID: number) => {
          userDeletionHandle(userID);
        }}
      />
    </Box>
  );
};

export default Header;

const LearnMoreContainer = styled.div`
  padding: 5px 8px;
  border-radius: 6px;
  background: rgba(0, 0, 0, 0.03);

  display: flex;
  align-items: center;
  gap: 4px;

  cursor: pointer;
`;

const LearnMoreButtonText = styled.div`
  color: #111;
  font-size: 15px;
  font-weight: 400;
`;

const EstimatedCost = styled.span`
  display: inline-block;
  color: #111;
  font-size: 15px;
  font-weight: 500;
  line-height: 18px;
`;
